import { theme as antdTheme, ThemeConfig } from 'antd';
import type { ComponentToken as MenuComponentToken } from 'antd/es/menu/style';
import type { ComponentToken as AvatarComponentToken } from "antd/es/avatar/style";

export type { MenuComponentToken };

const FONT_FAMILY_MAIN = "'Roboto', sans-serif";
const FONT_SIZE_BASE = 14;
const BORDER_RADIUS_BASE = 8;

export const legacyTheme: Partial<ThemeConfig> = {
  token: {
    colorPrimary: '#55C2EF',
    colorInfo: '#55C2EF',
    colorFillSecondary: '#e4eef5',
    colorError: '#BC5656',
    colorSuccess: '#34BC74',
    colorWarning: '#E4801E',
  },
}

export const darkTheme: Partial<ThemeConfig> = {
  algorithm: [antdTheme.darkAlgorithm],
  token: {
    colorPrimary: '#1668dc',
    colorInfo: '#2f83dc',
    colorError: '#dc4446',
    colorTextBase: '#fff',
  },
}

export const lightTheme: ThemeConfig = {
  cssVar: { prefix: 'bc', key: 'light-theme' },

  token: {
    // Primary & Info (different blues)
    colorPrimary: '#0092FF',
    colorInfo: '#3496FF',

    // Error (orange-red per design spec)
    colorError: '#F36B10',
    // Success & Warning: use antd default algorithms (green/gold presets per design spec)

    // Brand-blue text base — drives all text, fill, and spotlight tokens
    colorTextBase: '#17191A',
    colorTextPlaceholder: '#6F8CAE',
    colorBorder: '#E4EEF5',

    // Typography
    fontFamily: FONT_FAMILY_MAIN,
    fontSize: FONT_SIZE_BASE,

    // Sizing & Radius
    borderRadius: BORDER_RADIUS_BASE,
  },

  components: {
    Checkbox: {
      controlInteractiveSize: 18,
    },
    Button: {
      paddingInlineLG: 28,
    },
    Input: {
      fontSizeLG: 14,
    },
    Select: {
      optionPadding: '9px 12px',
      fontSize: 12,
      fontSizeLG: 14,
      optionFontSize: 14,
      multipleItemBg: 'none',
    },
    TreeSelect: {
      titleHeight: 32,
    },
    Layout: {
      headerHeight: 56,
      headerBg: '#ffffff',
      headerPadding: '0 24px',
    },
    Steps: {
      navArrowColor: 'transparent',
    },
    Table: {
      headerBg: 'var(--bc-color-bg-container)',
      headerColor: '#6F8CAE',
      headerSplitColor: '#DDE9F1',
      rowHoverBg: '#F2F3F4',
    },
  },
};

/** Menu tokens for BetterComp gradient active indicator on light headers. */
const gradientHeaderMenuTokens: Partial<MenuComponentToken> = {
  horizontalItemSelectedColor: 'var(--bc-menu-item-color)',
  subMenuItemSelectedColor: 'var(--bc-menu-item-color)',
  horizontalItemSelectedBg: 'linear-gradient(90deg, #F24E1A, #FFA700)',
};

const gradientHeaderAvatarTokens: Partial<AvatarComponentToken> = {
  groupBorderColor: 'linear-gradient(90deg, #F24E1A, #FFA700)',
}

/** Combined theme for dark blue navy header (Layout + Menu + Avatar tokens). */
export const blueGradientHeaderTheme: ThemeConfig = {
  algorithm: [antdTheme.darkAlgorithm],
  token: {
    colorTextBase: '#ffffff',
    colorTextPlaceholder: '#ffffff',
    colorBgBase: '#002952',
    colorBgElevated: '#002952',
    colorFillTertiary: 'rgba(255, 255, 255, 0.08)',
  },
  components: {
    Layout: {
      headerBg: '#002952',
      headerColor: '#ffffff',
    },
    Menu: {
      ...gradientHeaderMenuTokens,
      horizontalItemSelectedColor: '#ffffff',
      itemColor: 'rgba(255,255,255,0.65)',
      itemSelectedColor: '#ffffff',
      subMenuItemSelectedColor: '#ffffff',
      itemBg: '#002952',
    },
    Avatar: {
      ...gradientHeaderAvatarTokens,
      colorTextLightSolid: 'rgba(23, 25, 26, 0.65)'
    },
    Button: {
      colorTextSecondary: '#ffffff', // remove after cleaning Button component
    }
  },
};

/** Combined theme for light header with BetterComp gradient indicator. */
export const gradientHeaderTheme: ThemeConfig = {
  components: {
    Menu: gradientHeaderMenuTokens,
    Avatar: gradientHeaderAvatarTokens
  },
};

/** Custom token preset for AI gradient (orange-to-gold, matching brand gradient). */
export const aiGradientTokens = {
  aiGradientStart: '#F24E1A',
  aiGradientEnd: '#FFA700',
} as const;

export function createBrandTheme(
  overrides?: Partial<ThemeConfig>,
): ThemeConfig {
  if (!overrides) {
    return lightTheme;
  }

  return {
    ...lightTheme,
    ...overrides,
    token: {
      ...lightTheme.token,
      ...overrides.token,
    },
    components: {
      ...lightTheme.components,
      ...overrides.components,
    },
  };
}
